// @flow strict

import type { Group } from '@ansarada/ace-react/dist/ace/components/Select2/Select2.types';
import type { QuestionOptionType, QuestionGroup } from './types';
import type { TransactionTypeData } from './freetrial-data/transaction-type.json';
import type { IndustryData } from './freetrial-data/industry.json';
import type { OutcomeTypeData } from './freetrial-data/outcomes.json';
import outcomesData from './freetrial-data/outcomes.json';

export const generateQuestionOptions = (
  questionOptions: Array<QuestionOptionType>,
  type: string,
  parentValue: Array<string>,
): Array<QuestionOptionType> => {
  const filterParentIds = (ids: number | Array<number>, parentIds: Array<number>) => {
    return typeof ids === 'number'
      ? parentIds.includes(ids)
      : parentIds.filter(i => ids.includes(i)).length > 0;
  };

  const getQuestionOptionIdByValue = (value: string): number => {
    const questionOption = questionOptions.find(q => q.value === value);
    return questionOption ? questionOption.id : 0;
  };
  const ids = parentValue.map(getQuestionOptionIdByValue);
  const isGetAll = parentValue.length === 1 && parentValue[0].toLowerCase() === 'all';
  if (isGetAll) return questionOptions.filter(q => q.type === type);
  return questionOptions.filter(q => q.type === type && filterParentIds(ids, q.parentIds));
};

export const getTopLevelGroups = (
  questionOptions: Array<QuestionOptionType>,
): Array<QuestionGroup> => {
  const topLevelOptions = questionOptions.filter(q => !q.parentIds || q.parentIds.length === 0);
  if (!topLevelOptions) {
    return [];
  }
  return topLevelOptions.map(opt => {
    const options = questionOptions.filter(q => q.parentIds && q.parentIds.includes(opt.id));
    return {
      id: opt.id,
      text: opt.text,
      options,
    };
  });
};

export const generateOptions = (questionOptions: Array<QuestionOptionType>): Array<Group> => {
  const groups = getTopLevelGroups(questionOptions);
  if (!groups) {
    return [{ title: '', items: questionOptions.map(q => ({ label: q.text, value: q.value })) }];
  }
  return groups.map(opt => {
    return {
      title: opt.text,
      items: opt.options.map(q => ({ label: q.text, value: q.value })),
    };
  });
};

type SummaryProps = {|
  outcome: string,
  unsupportedOutcomeText: string,
  pathwayId: ?number,
|};

const OUTCOME_DATA_ROOM = 'Data Room';

export const findSummaryProps = (
  questionOptions: Array<QuestionOptionType>,
  question: string,
  type: string,
): ?SummaryProps => {
  const questionOption = questionOptions.find(q => q.value === question && q.type === type);
  if (questionOption && questionOption.outcome) {
    return {
      outcome: questionOption.outcome,
      unsupportedOutcomeText: questionOption.unsupportedOutcomeText || '',
      pathwayId: questionOption.pathwayId,
    };
  }
  return null;
};

// We're getting outcome from offered answers. The following code is corresponding to https://ansarada.atlassian.net/wiki/spaces/CAP/pages/1125810584/Free+Trial+Onboarding+Questions+Selections+Mappings
// We can't simply check the outcome by order as even we get outcome of pathway in previous step, it still could be override by final step.
export const getSummaryProps = (
  questionOptions: Array<QuestionOptionType>,
  question1: string,
  question2: string,
  question2Part2: string,
  question3: string,
): SummaryProps => {
  const result = findSummaryProps(questionOptions, question3, 'question3');
  if (result && result.outcome === OUTCOME_DATA_ROOM) {
    return result;
  }
  return (
    findSummaryProps(questionOptions, question1, 'question1') ||
    findSummaryProps(questionOptions, question2, 'question2') ||
    findSummaryProps(questionOptions, question2Part2, 'question2part2') || {
      outcome: OUTCOME_DATA_ROOM,
      unsupportedOutcomeText: '',
      pathwayId: undefined,
    }
  );
};

const DEFAULT_OUTCOME = { outcomeTypeName: '', outcomeSubTypeName: '', transactionTypeName: '' };
export const getOutcomeNames = (
  data: OutcomeTypeData[],
  outcomeId: string,
  subOutcomeId: string,
  transactionId: string,
) => {
  const outcome = data.find(o => o.OutcomeTypeID === outcomeId);
  if (!outcome) return DEFAULT_OUTCOME;
  const subOutcome = outcome.OutcomeSubTypes.find(
    sub => sub.OutcomeSubTypeID === subOutcomeId && sub.TransactionTypeID === transactionId,
  );
  if (!subOutcome) return DEFAULT_OUTCOME;
  return {
    outcomeTypeName: outcome.OutcomeTypeName,
    outcomeSubTypeName: subOutcome.OutcomeSubTypeGroupName || subOutcome.OutcomeSubTypeName,
    transactionTypeName: subOutcome.TransactionTypeName,
  };
};

export type chargebeeUserData = {|
  enabledTransactionTypeIndustryQuestions: boolean,
  question1: string,
  question2: string,
  question3: string,
  question2Part2: string,
  question2Part3: string,
  question4: string,
  outcome: string,
  pathwayId: ?number,
|};

export const getChargebeeData = (
  userData: chargebeeUserData,
): {
  [string]: ?string | number,
} => {
  const {
    enabledTransactionTypeIndustryQuestions,
    question1,
    question2,
    question3,
    question2Part2,
    question2Part3,
    question4,
    outcome,
    pathwayId,
  } = userData;

  if (enabledTransactionTypeIndustryQuestions) {
    const transactionTypeId = question2.split('|')[0];
    const isTypeOther = transactionTypeId === '0';
    const transactionTypeOtherName = question2.split('|')[2];
    const outcomeSubTypeId = question2.split('|')[1];
    const outcomeTypeId = question2.split('|')[3];
    const { outcomeTypeName, outcomeSubTypeName, transactionTypeName } = getOutcomeNames(
      outcomesData,
      outcomeTypeId,
      outcomeSubTypeId,
      transactionTypeId,
    );
    const industryGroupId = question2Part2.split('|')[0];
    const industryGroupName = question2Part2.split('|')[1];
    const subIndustryId = question2Part3.split('|')[0];
    const subIndustryName = question2Part3.split('|')[1];

    return {
      CustomerType: question1,
      OutcomeType: outcomeTypeName,
      OutcomeSubtype: outcomeSubTypeName,
      TransactionTypeId: isTypeOther ? null : Number(transactionTypeId),
      TransactionType: transactionTypeName,
      TransactionTypeOther: isTypeOther ? transactionTypeOtherName : '',
      IndustryId: Number(industryGroupId),
      Industry: industryGroupName,
      SubIndustryId: Number(subIndustryId),
      SubIndustry: subIndustryName,
      OutcomeStage: question3,
      ExtraRequest: question4,
    };
  }

  const chargebeeData = {
    CustomerType: question1,
    OutcomeType: question2,
    OutcomeSubtype: question2Part2,
    OutcomeStage: question3,
    ExtraRequest: question4,
  };

  return outcome === OUTCOME_DATA_ROOM
    ? chargebeeData
    : {
        ...chargebeeData,
        PathwayId: pathwayId,
        PathwayName: outcome,
      };
};

// From https://tools.ietf.org/html/rfc3986#appendix-B
const URIRegex = /^(([^:/?#]+):)?(\/\/([^/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?/;
export const getReferrerPathname = (url: string): ?string => {
  const parsed = URIRegex.exec(url);
  return parsed && parsed[5];
};

export const getTransactionType = (data: TransactionTypeData[]) => {
  const activeType = data.filter(t => t.Active === 'True');
  const transactionType: QuestionOptionType[] = [];

  activeType.forEach(t => {
    t.OutcomeOptions.forEach(opt => {
      transactionType.push({
        id: Number(t.TransactionTypeID),
        type: 'transactionType',
        value: `${t.TransactionTypeID}|${opt.OutcomeSubTypeId}|${opt.DisplayText}`,
        text: opt.DisplayText,
        parentIds: [],
      });
    });
  });

  return transactionType;
};

type Option = {|
  value: string,
  label: string,
|};

export const getOutcomes = (data: OutcomeTypeData[]): Option[] => {
  return data.map(outcome => ({
    value: outcome.OutcomeTypeID,
    label: outcome.OutcomeTypeName,
  }));
};

export const getSubOutcomes = (data: OutcomeTypeData[], outcomeId: string): Option[] => {
  const outcome = data.find(o => o.OutcomeTypeID === outcomeId);
  if (outcome) {
    return outcome.OutcomeSubTypes.map(subOutcome => ({
      value: `${subOutcome.TransactionTypeID}|${subOutcome.OutcomeSubTypeID}|${
        subOutcome.OutcomeSubTypeName
      }|${outcomeId}`,
      label: subOutcome.OutcomeSubTypeName,
    }));
  }
  return [];
};

export const getIndustry = (data: IndustryData[]) => {
  const ids = new Set();
  const activeIndsutryGroup: QuestionOptionType[] = [];
  const activeSubIndustry: QuestionOptionType[] = [];

  data.forEach(i => {
    if (i.GicsIndustryGroupActive === 'False') return;
    if (!ids.has(i.GicsIndustryGroupCode)) {
      ids.add(i.GicsIndustryGroupCode);
      activeIndsutryGroup.push({
        id: Number(i.GicsIndustryGroupCode),
        type: 'industryGroup',
        value: `${i.GicsIndustryGroupCode}|${i.GicsIndustryGroupName}`,
        text: i.GicsIndustryGroupName,
        parentIds: [],
      });
    }
    if (i.GicsSubIndustryActive === 'True') {
      activeSubIndustry.push({
        id: Number(i.GicsSubIndustryCode),
        type: 'industryName',
        value: `${i.GicsSubIndustryCode}|${i.GicsSubIndustryName}`,
        text: i.GicsSubIndustryName,
        parentIds: [],
      });
    }
  });

  return { activeIndsutryGroup, activeSubIndustry };
};

export const getMsStartupQueryParams = (
  enabledMsStartupSignup: boolean,
  enabledStartupConferenceSignup: boolean,
) => {
  if (enabledMsStartupSignup) return '&ms_startup_signup=1';
  if (enabledStartupConferenceSignup) return '&startup_conference_signup=1';

  return '';
};
